import { useState } from "react";
import { AlertCircle, CircleDollarSign, Package, Plus, Repeat, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Field, KpiCard, NativeSelect, PageHeader } from "@/components/common/Page";
import { EmptyState, ErrorState, LoadingState } from "@/components/common/States";
import { PaymentsTable, PackageCard } from "@/components/finance/PaymentsTable";
import { PackageFormDialog, PaymentFormDialog, RecurringFormDialog } from "@/components/finance/FinanceForms";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { brl, PAYMENT_STATUS, todayISO } from "@/lib/format";

const Summary = ({ month }) => {
  const { data, error } = useFetch(`/finance/summary?month=${month}`);
  if (error) return <ErrorState error={error} />;
  if (!data) return <LoadingState rows={1} />;
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
      <KpiCard label="Receita do mês" value={brl(data.receita_mes)} icon={Wallet} tone="text-accent" testId="fin-kpi-month-revenue" />
      <KpiCard label="Receita prevista" value={brl(data.prevista)} icon={TrendingUp} testId="fin-kpi-expected" />
      <KpiCard label="Valores recebidos" value={brl(data.recebido)} icon={CircleDollarSign} tone="text-accent" testId="fin-kpi-received" />
      <KpiCard label="Valores pendentes" value={brl(data.pendente)} icon={CircleDollarSign} tone="text-amber-500" testId="fin-kpi-pending" />
      <KpiCard label="Valores atrasados" value={brl(data.atrasado)} icon={AlertCircle} tone="text-destructive" testId="fin-kpi-late" />
    </div>
  );
};

const PaymentsTab = ({ month, version, bump }) => {
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  const { data, loading, error, reload } = useFetch(`/payments?start=${month}-01&end=${month}-${last}${status ? `&status=${status}` : ""}&v=${version}`);
  const changed = () => { reload(); bump(); };
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <Field label="Status" className="sm:w-48">
          <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="payments-status-filter">
            <option value="">Todos</option>
            {Object.entries(PAYMENT_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </NativeSelect>
        </Field>
        <Button onClick={() => setOpen(true)} data-testid="new-payment-btn"><Plus className="mr-2 h-4 w-4" />Novo pagamento</Button>
      </div>
      {error ? <ErrorState error={error} onRetry={reload} /> : loading && !data ? <LoadingState /> : data.length === 0
        ? <EmptyState icon={Wallet} title="Nenhum pagamento neste mês" description="Registre pagamentos ou configure mensalidades recorrentes." testId="payments-empty" />
        : <PaymentsTable payments={data} onChanged={changed} />}
      <PaymentFormDialog open={open} onOpenChange={setOpen} onSaved={changed} />
    </div>
  );
};

const RecurringTab = ({ bump }) => {
  const { data, loading, error, reload } = useFetch("/recurring");
  const profile = useFetch("/profile");
  const [edit, setEdit] = useState(null);
  const stop = async (r) => {
    try {
      await api.delete(`/recurring/${r.id}`);
      toast.success("Mensalidade encerrada");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const changed = () => { reload(); bump(); };
  const active = (data || []).filter((r) => r.active);
  return (
    <div className="space-y-4">
      <Button onClick={() => setEdit("new")} data-testid="new-recurring-btn"><Repeat className="mr-2 h-4 w-4" />Nova mensalidade</Button>
      {error ? <ErrorState error={error} /> : loading && !data ? <LoadingState /> : active.length === 0 ? (
        <EmptyState icon={Repeat} title="Nenhuma mensalidade configurada" description="Ex.: João — R$ 300,00 — vencimento todo dia 10." testId="recurring-empty" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {active.map((r) => (
            <div key={r.id} className="rounded-xl border bg-card p-5" data-testid={`recurring-card-${r.id}`}>
              <p className="font-heading font-semibold">{r.student_name}</p>
              <p className="mt-2 font-mono text-2xl font-semibold text-primary">{brl(r.amount)}</p>
              <p className="text-sm text-muted-foreground">Vencimento todo dia {r.due_day}</p>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEdit(r)} data-testid={`recurring-edit-${r.id}`}>Editar</Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => stop(r)} data-testid={`recurring-stop-${r.id}`}>Encerrar</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <RecurringFormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} recurring={edit === "new" ? null : edit}
        defaultDueDay={profile.data?.default_due_day || 10} onSaved={changed} />
    </div>
  );
};

const PackagesTab = ({ bump }) => {
  const { data, loading, error, reload } = useFetch("/packages");
  const [open, setOpen] = useState(false);
  const cancel = async (p) => {
    try {
      await api.post(`/packages/${p.id}/cancel`);
      toast.success("Pacote cancelado");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)} data-testid="new-package-btn"><Package className="mr-2 h-4 w-4" />Vender pacote</Button>
      {error ? <ErrorState error={error} /> : loading && !data ? <LoadingState /> : data.length === 0
        ? <EmptyState icon={Package} title="Nenhum pacote vendido" description="Ex.: Pacote 10 aulas por R$ 500,00." testId="packages-empty" />
        : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{data.map((p) => <PackageCard key={p.id} pkg={p} onCancel={cancel} />)}</div>}
      <PackageFormDialog open={open} onOpenChange={setOpen} onSaved={() => { reload(); bump(); }} />
    </div>
  );
};

export default function Finance() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [version, setVersion] = useState(0);
  const bump = () => setVersion((v) => v + 1);
  return (
    <div className="animate-in fade-in duration-300">
      <PageHeader title="Financeiro" subtitle="Pagamentos, mensalidades e pacotes"
        actions={<Input type="month" value={month} onChange={(e) => setMonth(e.target.value || todayISO().slice(0, 7))} className="w-44" data-testid="finance-month-input" />} />
      <Summary key={`${month}-${version}`} month={month} />
      <Tabs defaultValue="pagamentos">
        <TabsList className="mb-4">
          <TabsTrigger value="pagamentos" data-testid="finance-tab-payments">Pagamentos</TabsTrigger>
          <TabsTrigger value="mensalidades" data-testid="finance-tab-recurring">Mensalidades</TabsTrigger>
          <TabsTrigger value="pacotes" data-testid="finance-tab-packages">Pacotes</TabsTrigger>
        </TabsList>
        <TabsContent value="pagamentos"><PaymentsTab month={month} version={version} bump={bump} /></TabsContent>
        <TabsContent value="mensalidades"><RecurringTab bump={bump} /></TabsContent>
        <TabsContent value="pacotes"><PackagesTab bump={bump} /></TabsContent>
      </Tabs>
    </div>
  );
}
